import * as React from "react";
import { Form, Alert, FormGroup, Spinner, Button, FormText, Row, Col, Container } from "reactstrap";
import { useChanges } from "../../shared/useChanges";
import { useValidatorCallback } from "pojo-validator-react";
import { ValidatedInput } from "pojo-validator-reactstrap";
import { ButtonAsync } from "reactstrap-buttonasync"
import { useResetPasswordCallback } from "../../api/account";
import { ResetPassword as ResetPasswordModel } from '../../api/account/models/ResetPassword';
import { useTranslation } from "react-i18next";
import { LoadingIndicator } from "../shared/LoadingIndicator";
import { LinkContainer } from "react-router-bootstrap";
import { usePasswordValidation } from "../../shared/passwordValidation";
import './ResetPassword.scss';

/**
 * Reset the password (after following a unique link from an email).
 */
export const ResetPassword = () => {
    const { t } = useTranslation();
    const [hasResetPassword, setHasResetPassword] = React.useState<boolean>(false);
    const [resetPassword, { isExecuting: isResettingPassword, errors: resetPasswordErrors }] = useResetPasswordCallback();
    const { checkPassword, passwordRequirements } = usePasswordValidation();

    const { model, change } = useChanges<ResetPasswordModel>({ password: '', confirmPassword: '' });
    const [validate, validationErrors] = useValidatorCallback((validation, fieldsToCheck) => {
        if (!model) {
            return;
        }

        const rules = {
            password: () => !model.password ? t('resetPassword.passwordRequired', 'Password is required')
                : checkPassword(model.password).errorDescription,
            confirmPassword: () => model.confirmPassword !== model.password ? t('register.passwordsDoNotMatch', 'The password and confirmation password do not match') : '',
        };

        validation.checkRules(rules, fieldsToCheck);
    }, [model]);

    // Render the UI.
    //

    // If we're not ready, show the loading indicator.
    if (!model) {
        return (<LoadingIndicator />);
    }

    return (
        <Container fluid className="reset-password-page">
            <Row className="reset-password-row">
                <Col xs={12} sm={5} className="login-screen-target-column">
                    <div className="login-target-image"></div>
                </Col>
                <Col xs={12} sm={1}>
                </Col>
                <Col xs={12} sm={5} className="login-screen-input-column">
                    <Row className="impacts-logo-login-row">
                        <div className="impacts-logo-login-image"></div>
                    </Row>
                    <Row>
                        <h1>{t('resetPassword.resetPasswordHeading', 'Reset Password')}</h1>
                    </Row>
                    <Row>
                        <Col>
                            <p>
                                {t('resetPassword.thanksForConfirmingEmail', 'Thanks for confirming your email.  You can now set a new password.')}
                            </p>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form onSubmit={async e => {
                                e.preventDefault();
                                if (!validate()) {
                                    return;
                                }

                                const ok = await resetPassword(model.password);
                                setHasResetPassword(ok);
                            }}>
                                {
                                    resetPasswordErrors ? (
                                        <Alert color="danger">
                                            {resetPasswordErrors}
                                        </Alert>
                                    ) : null
                                }
                                {
                                    hasResetPassword ? (
                                        <Alert color="success">
                                            <Row>
                                                <Col>
                                                    {t('resetPassword.passwordHasBeenReset', 'Your password has been reset.')}
                                                </Col>
                                                <Col xs="auto">
                                                    <LinkContainer to="/authentication/login">
                                                        <Button color="success">
                                                            {t('confirmEmail.signInToContinue', 'Please sign in to continue')}
                                                        </Button>
                                                    </LinkContainer>
                                                </Col>
                                            </Row>
                                        </Alert>
                                    ) : null
                                }

                                <FormGroup>
                                    <ValidatedInput placeholder={t('resetPassword.password', 'Password')} type="password" name="password" autoComplete="new-password" value={model.password} onChange={e => change({ password: e.currentTarget.value })} onBlur={e => validate('password')} validationErrors={validationErrors['password']} />
                                    <FormText color="black">
                                        {passwordRequirements}
                                    </FormText>
                                </FormGroup>
                                <FormGroup>
                                    <ValidatedInput placeholder={t('resetPassword.confirmPassword', 'Confirm password')} type="password" name="confirmPassword" autoComplete="new-password" value={model.confirmPassword} onChange={e => change({ confirmPassword: e.currentTarget.value })} onBlur={e => validate('confirmPassword')} validationErrors={validationErrors['confirmPassword']} />
                                </FormGroup>
                                <Row className="sign-in-class">
                                    <Row className="sign-in-button">

                                        <ButtonAsync type="submit" color="secondary" isExecuting={isResettingPassword}
                                            executingChildren={<><Spinner size="sm" /> {t('resetPassword.resetting', 'Resetting password...')}</>}>
                                            {t('resetPassword.reset', 'Reset password')}
                                        </ButtonAsync>
                                    </Row>
                                </Row>
                            </Form>
                        </Col>
                    </Row>
                </Col>
                <Col xs={12} sm={1}>
                    
                </Col>
            </Row>
        </Container>
    );
};
